import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card, Form, Input, Select, Button, Space, Divider,
  message, Spin, Row, Col, Typography, Alert
} from 'antd';
import { SaveOutlined, CheckOutlined } from '@ant-design/icons';
import { useStores } from '../../stores';
import RichTextEditor from '../../components/RichTextEditor';
import ImageUpload from '../../components/ImageUpload';
import BackButton from '../../components/BackButton';

const { Title, Text } = Typography;

const ProductEnrich = observer(() => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminProductStore } = useStores();
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    title_en: '',
    title_el: '',
    description_en: '',
    description_el: '',
    category: null,
    image1: null,
    image2: null,
    image3: null,
    image4: null,
  });

  const [submitting, setSubmitting] = useState(false);
  const [referrer, setReferrer] = useState(null);

  // Helper to extract image URL from VersatileImageField object
  const getImageUrl = (imageField) => {
    if (!imageField) return null;

    // VersatileImageField returns an object with sizes
    if (typeof imageField === 'object' && !(imageField instanceof File)) {
      return imageField.medium || imageField.large || imageField.full_size || imageField.thumbnail;
    }

    // Fallback for direct URL string or File objects
    return imageField;
  };

  useEffect(() => {
    // Store referrer for navigation after save
    const state = window.history.state;
    if (state && state.usr && state.usr.from) {
      setReferrer(state.usr.from);
    }

    loadProduct();
    loadCategories();
  }, [id]);

  const loadProduct = async () => {
    try {
      await adminProductStore.fetchProductById(id);
      const product = adminProductStore.currentProduct;
      if (product) {
        setFormData({
          title_en: product.title_en || '',
          title_el: product.title_el || '',
          description_en: product.description_en || '',
          description_el: product.description_el || '',
          category: product.category?.id || null,
          image1: product.main_image || null,
          image2: product.image_2 || null,
          image3: product.image_3 || null,
          image4: product.image_4 || null,
        });
        form.setFieldsValue({
          title_en: product.title_en || '',
          title_el: product.title_el || '',
          category: product.category?.id || null,
        });
      }
    } catch (error) {
      message.error(t('admin.productEnrich.loadFailed'));
    }
  };

  const loadCategories = async () => {
    try {
      await adminProductStore.fetchCategories();
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleSave = async (markComplete = false) => {
    try {
      await form.validateFields();
      setSubmitting(true);

      const enrichData = {
        ...formData,
        is_marketing_complete: markComplete,
      };

      await adminProductStore.enrichProduct(id, enrichData);
      message.success(markComplete ?
        t('admin.productEnrich.enrichedSuccess') :
        t('admin.productEnrich.savedSuccess'));

      // Navigate back to referrer or default to /admin/products
      const targetPath = referrer || '/admin/products';
      navigate(targetPath);
    } catch (error) {
      if (error.errorFields) {
        message.error(t('admin.productEnrich.requiredFields'));
      } else {
        message.error(t('admin.productEnrich.saveFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!adminProductStore.currentProduct) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const product = adminProductStore.currentProduct;

  return (
    <div style={{ padding: '24px' }}>
      <BackButton to={referrer || '/admin/products'} label={t('common.back')} />

      <Card>
        <Title level={3}>{t('admin.productEnrich.title', { id: product.id })}</Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={() => {}}
        >
          {/* Product Names */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('admin.productEnrich.titleEn')}
                name="title_en"
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input
                  placeholder={t('admin.productEnrich.titleEnPlaceholder')}
                  value={formData.title_en}
                  onChange={(e) => handleFieldChange('title_en', e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('admin.productEnrich.titleEl')}
                name="title_el"
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input
                  placeholder={t('admin.productEnrich.titleElPlaceholder')}
                  value={formData.title_el}
                  onChange={(e) => handleFieldChange('title_el', e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Category */}
          <Form.Item
            label={t('admin.products.category')}
            name="category"
            rules={[{ required: true, message: t('admin.productEnrich.selectCategory') }]}
          >
            <Select
              placeholder={t('admin.productEnrich.selectCategoryPlaceholder')}
              value={formData.category}
              onChange={(value) => handleFieldChange('category', value)}
            >
              {adminProductStore.categories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {i18n.language === 'el' ? `${cat.name_el || cat.name_en} / ${cat.name_en}` : `${cat.name_en} / ${cat.name_el || cat.name_en}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Divider>{t('admin.productEnrich.descriptions')}</Divider>

          {/* English Description */}
          <Form.Item label={t('admin.productEnrich.descriptionEn')}>
            <RichTextEditor
              value={formData.description_en}
              onChange={(value) => handleFieldChange('description_en', value)}
              placeholder={t('admin.productEnrich.descriptionEnPlaceholder')}
              height={300}
            />
          </Form.Item>

          {/* Greek Description */}
          <Form.Item label={t('admin.productEnrich.descriptionEl')}>
            <RichTextEditor
              value={formData.description_el}
              onChange={(value) => handleFieldChange('description_el', value)}
              placeholder={t('admin.productEnrich.descriptionElPlaceholder')}
              height={300}
            />
          </Form.Item>

          <Divider>{t('admin.productEnrich.imagesSection')}</Divider>

          {/* Images */}
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item label={t('admin.productEnrich.image1')}>
                <ImageUpload
                  value={formData.image1}
                  onChange={(file) => handleFieldChange('image1', file)}
                  label={t('admin.productEnrich.uploadImage1')}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={t('admin.productEnrich.image2')}>
                <ImageUpload
                  value={formData.image2}
                  onChange={(file) => handleFieldChange('image2', file)}
                  label={t('admin.productEnrich.uploadImage2')}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={t('admin.productEnrich.image3')}>
                <ImageUpload
                  value={formData.image3}
                  onChange={(file) => handleFieldChange('image3', file)}
                  label={t('admin.productEnrich.uploadImage3')}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={t('admin.productEnrich.image4')}>
                <ImageUpload
                  value={formData.image4}
                  onChange={(file) => handleFieldChange('image4', file)}
                  label={t('admin.productEnrich.uploadImage4')}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Divider />
          <Space>
            <Button
              type="default"
              icon={<SaveOutlined />}
              onClick={() => handleSave(false)}
              loading={submitting}
              size="large"
            >
              {t('admin.productEnrich.saveDraft')}
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => handleSave(true)}
              loading={submitting}
              size="large"
            >
              {t('admin.productEnrich.saveAndComplete')}
            </Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
});

export default ProductEnrich;
